import React from 'react';
import {
  default as MuiStepper,
  type StepperTypeMap as MuiStepperTypeMap,
  type StepperProps as MuiStepperProps,
} from '@mui/material/Stepper';
import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';
import { DistributiveOmit } from '@mui/types';

/**
 * Props that we do not want to include with Stepper.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type StepperBlockedProps = keyof Pick<
  MuiStepperProps,
  'elevation' | 'variant' | 'square' | 'connector'
>;

export interface StepperOwnProps {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  connector?: MuiStepperProps['connector'];
}

export interface StepperTypeMap<
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {},
  RootComponent extends React.ElementType = MuiStepperTypeMap['defaultComponent']
> {
  props: AdditionalProps &
    DistributiveOmit<MuiStepperProps, StepperBlockedProps> &
    StepperOwnProps;
  defaultComponent: RootComponent;
}

export type StepperProps<
  RootComponent extends React.ElementType = StepperTypeMap['defaultComponent'],
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {}
> = OverrideProps<
  StepperTypeMap<AdditionalProps, RootComponent>,
  RootComponent
> & {
  component?: React.ElementType;
};

/**
 * A stepper is a progress indicator that shows where the customer is along the steps of a journey.
 *
 * Demos:
 *
 * - [Stepper](https://unify.workiva.org/components/stepper?tab=code)
 *
 * API:
 *
 * - [Stepper API](https://unify.workiva.org/components/stepper?tab=props)
 */
export const Stepper: OverridableComponent<StepperTypeMap> = MuiStepper;
